import React, { useEffect, useState } from 'react';
import { getNotApprovedSessionTransactionsByLocalId } from '../../../../../../crud/payments_api/payment_transactions';
import { FaExclamationTriangle, FaClock } from 'react-icons/fa';
import ApproveTransaction from './components/ApproveCash/ApproveTransaction';
import { formatCurrency } from '../../../../../../utils/functions';
import { getLocalCajaManagementByLocalId } from '../../../../../../crud/management_api/locales_caja_management';

export interface Transaction {
  transaction_id: number;
  external_id: string | null;
  processor_id: number;
  processor_name: string;
  user_id: number;
  user_name: string;
  local_id: number;
  payment_type_id: number;
  payment_type_name: string;
  timestamp: string; // ISO 8601 format
  local_timestamp: string; // ISO 8601 format
  transaction_amount: number;
  moneda_id: number;
  client_id: number | null;
  client_name: string | null;
  approved: boolean;
  canceled: boolean | null;
  session_id: number;
  transaction_metadata: Record<string, any> | null; // Assuming metadata is JSON or object-like
}

interface PendingCashProps {
    cajas: any;
    setCajas: (cajas: any[]) => void;
    refreshCajas?: () => void;
    closeModalPendingCash?: () => void;
}

const PendingCash: React.FC<PendingCashProps> = ({cajas, setCajas, refreshCajas, closeModalPendingCash}) => {
  const [pendingTransactions, setPendingTransactions] = useState<Transaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showApproveTransactionModal, setShowApproveTransactionModal] = useState(false);
  const [elapsedTimes, setElapsedTimes] = useState<Record<number, string>>({});

  // Update local cajas state when parent cajas change
  useEffect(() => {
    if (cajas && cajas.length > 0) {
      // Cajas already updated from parent
    }
  }, [cajas]);


  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const localId = user ? Number(user.local_id) : 0;

    getLocalCajaManagementByLocalId(localId)
      .then((response) => {
        setCajas(response.data);
      })
      .catch((error) => {
        console.error('Error fetching cajas: ', error);
      });

    getNotApprovedSessionTransactionsByLocalId(localId)
      .then((response) => {
        setPendingTransactions(response.data);
        if (response.data.length === 0 && typeof closeModalPendingCash === 'function') {
          closeModalPendingCash();
        }
      })
      .catch((error) => {
        console.error('Error getting pending transactions: ', error);
      });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const updatedElapsedTimes: Record<number, string> = {};
      pendingTransactions.forEach((transaction) => {
        const transactionTime = new Date(transaction.local_timestamp);
        const diffInSeconds = Math.floor((now.getTime() - transactionTime.getTime()) / 1000);

        const hours = Math.floor(diffInSeconds / 3600);
        const minutes = Math.floor((diffInSeconds % 3600) / 60);

        updatedElapsedTimes[transaction.transaction_id] = `${hours}h ${minutes}m`;
      });
      setElapsedTimes(updatedElapsedTimes);
    }, 1000);

    return () => clearInterval(interval);
  }, [pendingTransactions]);

  const handleApproveTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowApproveTransactionModal(true);
  };

  return (
    <>
      {pendingTransactions.length > 0 && (
        <div className="mp-pending-section my-4">
          <div className="mp-pending-header">
            <h3 className="flex items-center gap-2 text-sm font-semibold m-0" style={{ color: 'var(--text-color)' }}>
              <FaExclamationTriangle className="text-[#FC7556]" />
              Transacciones Pendientes
            </h3>
            <span className="mp-pending-count">{pendingTransactions.length}</span>
          </div>
          
          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3 p-4">
            {pendingTransactions.map((transaction) => (
              <div
                key={transaction.transaction_id}
                className="mp-mobile-card cursor-pointer hover:-translate-y-0.5 hover:shadow-md"
                onClick={() => handleApproveTransaction(transaction)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #FC7556, #E85B44)' }}>
                      {transaction.user_name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium" style={{ color: 'var(--text-color)' }}>{transaction.user_name}</span>
                  </div>
                  <span className="mp-badge mp-badge-warning flex items-center gap-1">
                    <FaExclamationTriangle className="text-[10px]" />
                    {formatCurrency(transaction.transaction_amount)}
                  </span>
                </div>
                
                <div className="mb-2">
                  <span className="mp-chip text-[10px]">
                    <FaClock className="text-[#FC7556]" />
                    {elapsedTimes[transaction.transaction_id] || 'Calculando...'}
                  </span>
                </div>
                
                <button className="mp-btn-primary w-full text-xs py-2" onClick={(e) => {
                  e.stopPropagation();
                  handleApproveTransaction(transaction);
                }}>
                  Recibir
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {selectedTransaction && (
        <ApproveTransaction
          cajas={cajas}
          setCajas={setCajas}
          transaction={selectedTransaction}
          show={showApproveTransactionModal}
          setShow={setShowApproveTransactionModal}
          transactions={pendingTransactions}
          setTransactions={(newTransactions) => {
            setPendingTransactions(newTransactions);
            if (newTransactions.length === 0 && typeof closeModalPendingCash === 'function') {
              closeModalPendingCash();
            }
          }}
          refreshCajas={refreshCajas}
        />
      )}
    </>
  );
};

export default PendingCash;
